import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import type { EraDef } from '../content/types';
import { wrapCoord, wrapDelta } from '../sim/world';

export type Quality = 'low' | 'med' | 'high';

/** Vignette + chromatic aberration post shader (cheap, single pass). */
const PostShader = {
  uniforms: {
    tDiffuse: { value: null },
    vignette: { value: 0.35 },
    aberration: { value: 0.0 },
    warp: { value: 0.0 },
    time: { value: 0 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float vignette;
    uniform float aberration;
    uniform float warp;
    uniform float time;
    varying vec2 vUv;
    void main() {
      vec2 uv = vUv;
      vec2 c = uv - 0.5;
      // time-warp: radial ripple
      if (warp > 0.0) {
        float r = length(c);
        uv += normalize(c + 1e-5) * sin(r * 40.0 - time * 12.0) * 0.006 * warp;
      }
      float ab = aberration * 0.012;
      vec3 col;
      if (ab > 0.0) {
        col.r = texture2D(tDiffuse, uv + c * ab).r;
        col.g = texture2D(tDiffuse, uv).g;
        col.b = texture2D(tDiffuse, uv - c * ab).b;
      } else {
        col = texture2D(tDiffuse, uv).rgb;
      }
      float v = smoothstep(0.85, 0.25, length(c) * (1.0 + vignette));
      col *= mix(1.0 - vignette, 1.0, v);
      gl_FragColor = vec4(col, 1.0);
    }
  `,
};

/**
 * Renderer: owns the WebGL context, scene, isometric camera, lights and the
 * post-processing chain. Quality levels:
 *  low  — no post, half-res on hi-dpi, no shadows
 *  med  — bloom + vignette, no shadows
 *  high — bloom + vignette + shadows, full pixel ratio
 */
export class Renderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly sun: THREE.DirectionalLight;
  readonly ambient: THREE.HemisphereLight;
  private composer: EffectComposer | null = null;
  private bloom: UnrealBloomPass | null = null;
  private post: ShaderPass | null = null;
  quality: Quality = 'med';

  // camera rig
  readonly camTarget = new THREE.Vector3();
  private camPos = new THREE.Vector3();
  camYaw = Math.PI / 4;
  camPitch = THREE.MathUtils.degToRad(52);
  camDistance = 30;
  reducedFlash = false;
  private shake = 0;
  private shakeVec = new THREE.Vector3();
  /** Half-size of the toroidal world (camera follow wraps across the seam). */
  worldHalf = 60;
  /** 0..1 intensity of time-warp visuals (chromatic aberration + ripple). */
  warp = 0;
  private time = 0;

  constructor(readonly canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = false;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.camera = new THREE.PerspectiveCamera(38, 1, 0.5, 200);
    this.scene.add(this.camera);

    this.ambient = new THREE.HemisphereLight(0xffffff, 0x223344, 0.9);
    this.scene.add(this.ambient);
    this.sun = new THREE.DirectionalLight(0xffffff, 1.2);
    this.sun.position.set(20, 40, 10);
    this.sun.castShadow = false;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.camera.left = -45;
    this.sun.shadow.camera.right = 45;
    this.sun.shadow.camera.top = 45;
    this.sun.shadow.camera.bottom = -45;
    this.sun.shadow.camera.far = 120;
    this.sun.shadow.bias = -0.0005;
    this.scene.add(this.sun);
    this.scene.add(this.sun.target);

    this.setQuality(this.quality);
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  /** Called after quality changes (view hooks the outline toggle here). */
  onQualityChange: ((q: Quality) => void) | null = null;

  setQuality(q: Quality): void {
    this.quality = q;
    this.onQualityChange?.(q);
    const dpr = window.devicePixelRatio || 1;
    this.renderer.setPixelRatio(q === 'low' ? Math.min(1, dpr) : q === 'med' ? Math.min(1.5, dpr) : Math.min(2, dpr));
    this.renderer.shadowMap.enabled = q === 'high';
    this.sun.castShadow = q === 'high';
    if (q === 'low') {
      this.composer = null;
      this.bloom = null;
      this.post = null;
    } else {
      this.composer = new EffectComposer(this.renderer);
      this.composer.addPass(new RenderPass(this.scene, this.camera));
      this.bloom = new UnrealBloomPass(new THREE.Vector2(innerWidth, innerHeight), q === 'high' ? 0.9 : 0.7, 0.5, 0.82);
      this.composer.addPass(this.bloom);
      this.post = new ShaderPass(PostShader);
      this.composer.addPass(this.post);
      this.composer.addPass(new OutputPass());
    }
    this.resize();
  }

  resize(): void {
    const w = innerWidth;
    const h = innerHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.composer?.setSize(w, h);
    this.bloom?.setSize(w, h);
  }

  /** Apply an era's palette/lighting. */
  applyEra(era: EraDef): void {
    const p = era.palette;
    this.scene.background = new THREE.Color(p.sky);
    this.scene.fog = new THREE.FogExp2(p.fog, p.fogDensity);
    this.worldHalf = era.environment.arenaRadius;
    this.ambient.color.set(p.ambient);
    this.ambient.groundColor.set(p.ground);
    this.sun.color.set(p.sun);
    this.sun.intensity = p.sunIntensity;
  }

  addShake(strength: number): void {
    this.shake = Math.min(1.5, this.shake + strength);
  }

  setCameraDistance(distance: number): void {
    this.camDistance = clampCameraDistance(distance);
  }

  /** Update the camera rig toward (x, z) in world space and draw. */
  render(targetX: number, targetZ: number, frameDt: number): void {
    this.time += frameDt;
    const h = this.worldHalf;
    const k = 1 - Math.exp(-8 * frameDt);
    this.camTarget.x += wrapDelta(targetX - this.camTarget.x, h) * k;
    this.camTarget.z += wrapDelta(targetZ - this.camTarget.z, h) * k;
    this.camTarget.x = wrapCoord(this.camTarget.x, h);
    this.camTarget.z = wrapCoord(this.camTarget.z, h);
    const cp = Math.cos(this.camPitch);
    this.camPos.set(
      this.camTarget.x + Math.cos(this.camYaw) * cp * this.camDistance,
      this.camTarget.y + Math.sin(this.camPitch) * this.camDistance,
      this.camTarget.z + Math.sin(this.camYaw) * cp * this.camDistance,
    );
    if (this.shake > 0) {
      const s = this.shake * 0.35;
      this.shakeVec.set((Math.random() - 0.5) * s, (Math.random() - 0.5) * s, (Math.random() - 0.5) * s);
      this.camPos.add(this.shakeVec);
      this.shake = Math.max(0, this.shake - frameDt * 4);
    }
    this.camera.position.copy(this.camPos);
    this.camera.lookAt(this.camTarget);

    // keep the shadow frustum centred on the action
    this.sun.position.set(this.camTarget.x + 20, 40, this.camTarget.z + 10);
    this.sun.target.position.copy(this.camTarget);

    if (this.post) {
      this.post.uniforms.aberration.value = this.warp;
      this.post.uniforms.warp.value = this.warp;
      this.post.uniforms.time.value = this.time;
    }
    if (this.bloom) this.bloom.strength = this.reducedFlash ? 0.28 : this.quality === 'high' ? 0.9 : 0.7;
    if (this.composer) this.composer.render();
    else this.renderer.render(this.scene, this.camera);
  }

  /** A scratch Vector3 for callers outside the render layer (keeps three out of ui/). */
  makeVec(): THREE.Vector3 {
    return new THREE.Vector3();
  }

  /** Project a world point to normalized screen coords (for UI overlays). */
  project(x: number, y: number, z: number, out: THREE.Vector3): THREE.Vector3 {
    out.set(x, y, z).project(this.camera);
    return out;
  }
}

export const MIN_CAMERA_DISTANCE = 26;
export const MAX_CAMERA_DISTANCE = 44;
export const DEFAULT_CAMERA_DISTANCE = 30;

export function clampCameraDistance(value: number): number {
  return Math.min(MAX_CAMERA_DISTANCE, Math.max(MIN_CAMERA_DISTANCE, Number.isFinite(value) ? value : DEFAULT_CAMERA_DISTANCE));
}
